import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { fetchIntegratedPlatform } from '../../integratedSlice';
import { fetchShipStationData, configureStore, resetConfigureState } from './shipstationSlice';
import ShipmentUpdateFlag from '../../../../General/ShipmentUpdateFlag';

const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    height: '100%'
}));

// Standard corporate outliner dictionary configuring premium input layouts
const enterpriseInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        '& fieldset': { borderColor: '#E2E8F0' },
        '&:hover fieldset': { borderColor: '#CBD5E1' },
        '&.Mui-focused fieldset': { 
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-root': {
        color: '#64748B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const ShipStationIntegration = ({ storeName, platformName, storeParam, storeId }) => {
    const dispatch = useDispatch();

    const profile = useSelector(state => state.auth);
    const shipstation = useSelector(state => state.shipstation && state.shipstation.progress);
    const shipstationconfigdetails = useSelector(state => state.shipstation && state.shipstation.configure);
    const [alert, setAlert] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
    const initialValues = {
        api_key: '',
        api_secret: '',
    }
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});

    const validator = (fieldValues) => {
        let temp = { ...errors }
        if ('api_key' in fieldValues)
            temp.api_key = fieldValues.api_key ? "" : "This field is required."
        if ('api_secret' in fieldValues)
            temp.api_secret = fieldValues.api_secret ? "" : "This field is required."

        setErrors({
            ...temp
        })

        if (fieldValues === values)
            return Object.values(temp).every(x => x == "")
    }

    const onInputChange = e => {
        const { name, value } = e.target
        setValues({
            ...values,
            [name]: value
        })
        validator({ [name]: value })
    }

    const updateCallBackValue = (updateValue) => {
        setValues({
            ...values,
            ['is_update_shipment']: updateValue.is_update_shipment,
            ['is_update_shipment_on_create']: updateValue.is_update_shipment_on_create,
            ['is_update_shipment_on_close']: updateValue.is_update_shipment_on_close,
        });
    };

    const handleSubmit = e => {
        e.preventDefault()
        const isValid = validator(values)
        if (isValid) {
            dispatch(configureStore(profile.profile.id,values));
        }
    }

    useEffect(() => {
        if (shipstation && shipstation.data
             && shipstation.data.api_key) {
            setValues({
                ...values,
                api_key: shipstation && shipstation.data
                && shipstation.data.api_key,
                api_secret: shipstation && shipstation.data &&
                shipstation.data.api_secret,
                is_update_shipment: shipstation && shipstation.data && shipstation.data.is_update_shipment,
                ['is_update_shipment_on_create']: shipstation && shipstation.data  && shipstation.data.is_update_shipment_on_create,
                ['is_update_shipment_on_close']: shipstation  && shipstation.data  && shipstation.data.is_update_shipment_on_close,
            })
        }
    }, [shipstation && shipstation.data && shipstation.data.api_key])

    const handleClose = () => {
        dispatch(resetConfigureState())
        setSnackOpen(false);
    };

    useEffect(() => {
        if (shipstationconfigdetails && shipstationconfigdetails.message) {
            setAlert(true)
            setSnackOpen(true)
        } else if (shipstationconfigdetails && shipstationconfigdetails.hasErrors == true) {
            setAlert(true)
            setSnackOpen(true)
        }
    }, [dispatch, shipstationconfigdetails && shipstationconfigdetails.message])

    useEffect(() => {
        if (storeName) {
            let apikey = profile.profile.api_token
            const data = { apikey: apikey, store_name: storeName, store_id: storeId }
            dispatch(fetchShipStationData(data));
        }
    }, [dispatch]);

    useEffect(() => {
        if (platformName) {
            setValues({
                ...values,
                api_key: '',
                api_secret: '',
                is_update_shipment: false
            });
        }
    }, [platformName]);

    useEffect(() => {       
        fetchIntegratedPlatform();
    }, [dispatch]);

    return (
        <>
         {alert &&
                <Snackbar
                    open={snackOpen}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert
                        variant="filled"
                        severity={shipstationconfigdetails && shipstationconfigdetails.hasErrors === true ? "error" : "success"}
                        sx={{ 
                            justifyContent: 'center', 
                            margin: "10px 0",
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <span > {shipstationconfigdetails && shipstationconfigdetails.message}</span>
                    </Alert>
                </Snackbar>
            }
            <Grid container spacing={0} justifyContent="center" sx={{ my: 4 }}>
                <Grid item xs={12} sm={10} md={8} lg={7}>
                    <Card
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: '16px',
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                            backgroundColor: '#FFFFFF'
                        }}
                    >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            <Item style={{ marginBottom: '0px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3.5 }}>
                                    
                                    {/* App Brand Identity Component Logo Display */}
                                    <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderRadius: '14px', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box
                                            component="img"
                                            src="/assets/images/integration/shipstation.png"
                                            alt="SHIPSTATION"
                                            sx={{ width: 'auto', height: '44px', objectFit: 'contain' }}
                                        />
                                    </Box>

                                    {/* Module Context Headline Header Text */}
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '18px', mb: 0.5, letterSpacing: '-0.3px' }}>
                                            Connect ShipStation Account
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748B', maxWidth: '380px', mx: 'auto', lineHeight: 1.5 }}>
                                            Provide your ShipStation production credential credentials below to link synchronous fulfillment logs.
                                        </Typography>
                                    </Box>

                                    {/* Central Form Inputs Layout Stack Grid */}
                                    <Box sx={{ width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                                        <TextField
                                            label="API KEY"
                                            name="api_key"
                                            size="small"
                                            value={values.api_key}
                                            onChange={onInputChange}
                                            error={!!errors.api_key}
                                            helperText={errors.api_key}
                                            variant="outlined"
                                            sx={enterpriseInputStyles}
                                        />

                                        <TextField
                                            label="API Secret"
                                            name="api_secret"
                                            size="small"
                                            value={values.api_secret}
                                            onChange={onInputChange}
                                            error={!!errors.api_secret}
                                            helperText={errors.api_secret}
                                            variant="outlined"
                                            sx={enterpriseInputStyles}
                                        />
                                    </Box>

                                    {/* Embedded Callback Event Configuration Layer Row */}
                                    <Box sx={{ width: '100%', borderTop: '1px solid #F1F5F9', pt: 2, mt: 1 }}>
                                        <ShipmentUpdateFlag
                                            updateFlag={values.is_update_shipment}
                                            onCreateFlag={values.is_update_shipment_on_create}
                                            onCloseFlag={values.is_update_shipment_on_close}
                                            updateCallBack={updateCallBackValue}
                                            message={" Do you want to update shipment information back to shipstation account?"}
                                        />
                                    </Box>

                                    {/* Action Trigger Submit Verification Control Area */}
                                    <Box className="sc--form--center" sx={{ width: '100%', display: 'flex', justifyContent: 'center', borderTop: '1px solid #F1F5F9', pt: 3 }}>
                                        <LoadingButton
                                            variant="contained"
                                            onClick={handleSubmit}
                                            loading={!!(shipstationconfigdetails && shipstationconfigdetails.loading)}
                                            endIcon={<SendOutlinedIcon sx={{ fontSize: '15px !important', ml: 0.5 }} />}
                                            sx={{
                                                px: 5,
                                                py: 1.25,
                                                borderRadius: '8px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                fontSize: '13.5px',
                                                boxShadow: 'none',
                                                backgroundColor: '#1976d2',
                                                '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                            }}
                                        >
                                            Configure
                                        </LoadingButton>
                                    </Box>

                                </Box>
                            </Item>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    )
}

export default ShipStationIntegration;